import { Controller } from "@hotwired/stimulus"

// Manages the inline feature picker on the playlist new/edit form.
//
// - Listens for car/region/version/year/model/search filter changes
// - Fetches the candidates partial from the server
// - Replaces the candidates table HTML
// - Tracks selected feature IDs as hidden inputs in the form (so the
//   selection persists across filter re-renders)
// - Updates the "X selecionadas" counter
export default class extends Controller {
  static targets = [
    "carSelect", "regionSelect",
    "versionSelect", "yearSelect", "modelSelect", "searchInput",
    "content", "counter", "hiddenIds", "checkbox"
  ]

  static values = {
    fetchUrl:      { type: String, default: "/admin/playlists/picker_candidates" },
    pickCarRegion: { type: String, default: "Pick a car and a region to see available features." },
    selectedOne:   { type: String, default: "1 selected" },
    selectedMany:  { type: String, default: "%{count} selected" }
  }

  connect() {
    this.page = 1
    this.selected = new Set()
    // Hydrate selection from any pre-existing hidden inputs (edit mode)
    if (this.hasHiddenIdsTarget) {
      this.hiddenIdsTarget.querySelectorAll("input[type=hidden]").forEach(el => {
        if (el.value) this.selected.add(el.value)
      })
    }
    this.updateCounter()
    // If region pre-set (e.g. query param or edit mode), filter car options
    if (this.hasRegionSelectTarget && this.regionSelectTarget.value) {
      this.filterCarsByRegion()
    }
    // Kick off an initial fetch so the picker renders immediately on edit.
    this.refreshIfReady()
  }

  // When region changes, filter the car dropdown to show only cars in
  // that region, then refresh the picker.
  regionChanged() {
    this.page = 1
    this.filterCarsByRegion()
    this.refreshIfReady()
  }

  filterCarsByRegion() {
    if (!this.hasRegionSelectTarget || !this.hasCarSelectTarget) return
    const regionId = this.regionSelectTarget.value
    const carSelect = this.carSelectTarget

    if (!regionId) {
      carSelect.disabled = true
      carSelect.value = ""
      return
    }

    let firstVisible = null
    let currentStillVisible = false

    Array.from(carSelect.options).forEach(opt => {
      if (!opt.value) {
        opt.hidden = true
        return
      }
      const regions = (opt.dataset.regions || "").split(",").filter(Boolean)
      const visible = regions.includes(regionId)
      opt.hidden = !visible
      if (visible) {
        firstVisible ||= opt
        if (opt.value === carSelect.value) currentStillVisible = true
      }
    })

    carSelect.disabled = false
    if (!currentStillVisible) {
      carSelect.value = firstVisible ? "" : ""
    }
  }

  // Filter inputs all call this
  refresh() {
    this.page = 1
    this.refreshIfReady()
  }

  debouncedRefresh() {
    clearTimeout(this._debounce)
    this._debounce = setTimeout(() => {
      this.page = 1
      this.refreshIfReady()
    }, 300)
  }

  goToPage(event) {
    event.preventDefault()
    const page = parseInt(event.currentTarget.dataset.page || "1", 10)
    if (!Number.isFinite(page) || page < 1) return

    this.page = page
    this.refreshIfReady()
  }

  refreshIfReady() {
    const carId    = this.carSelectTarget.value
    const regionId = this.regionSelectTarget.value

    if (!carId || !regionId) {
      this.contentTarget.innerHTML = `
        <p class="text-sm text-base-content/40 p-6 text-center">
          ${this._escape(this.pickCarRegionValue)}
        </p>`
      return
    }

    const params = new URLSearchParams({
      car_id: carId,
      region_id: regionId,
    })
    if (this.hasVersionSelectTarget && this.versionSelectTarget.value) params.set("pick_version_id", this.versionSelectTarget.value)
    if (this.hasYearSelectTarget    && this.yearSelectTarget.value)    params.set("pick_year", this.yearSelectTarget.value)
    if (this.hasModelSelectTarget   && this.modelSelectTarget.value)   params.set("pick_model_id", this.modelSelectTarget.value)
    if (this.hasSearchInputTarget   && this.searchInputTarget.value)   params.set("pick_q", this.searchInputTarget.value)
    params.set("page", String(this.page || 1))

    fetch(`${this.fetchUrlValue}?${params.toString()}`, {
      headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "text/html" }
    })
      .then(r => r.text())
      .then(html => {
        this.contentTarget.innerHTML = html
        this.reapplyChecks()
      })
  }

  // Re-check any visible checkbox whose value is in our selected set
  reapplyChecks() {
    this.contentTarget.querySelectorAll("input[type=checkbox][value]").forEach(cb => {
      cb.checked = this.selected.has(cb.value)
    })
  }

  // Called when a feature checkbox in the candidates list is clicked
  toggleFeature(event) {
    const id = event.currentTarget.value
    if (event.currentTarget.checked) {
      this.selected.add(id)
      this.addHiddenInput(id)
    } else {
      this.selected.delete(id)
      this.removeHiddenInput(id)
    }
    this.updateCounter()
  }

  addHiddenInput(id) {
    if (this.hiddenIdsTarget.querySelector(`input[value="${id}"]`)) return
    const input = document.createElement("input")
    input.type  = "hidden"
    input.name  = "feature_ids[]"
    input.value = id
    this.hiddenIdsTarget.appendChild(input)
  }

  removeHiddenInput(id) {
    const el = this.hiddenIdsTarget.querySelector(`input[value="${id}"]`)
    if (el) el.remove()
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      const n = this.selected.size
      this.counterTarget.textContent = n === 1
        ? this.selectedOneValue
        : this.selectedManyValue.replace("%{count}", n)
    }
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

}
